import type {
  CardDefinition,
  DepthValues,
  ObjectCardDefinition,
  SpellCardDefinition,
  SpellEffect,
  SpellTargetType,
} from "./types.js";

function objectCard(
  id: string,
  name: string,
  values: DepthValues,
  text: string,
  art: string,
  options: Pick<
    ObjectCardDefinition,
    | "persistent"
    | "returnsOnce"
    | "surfaceHolds"
    | "resonanceBonus"
    | "mergeBonus"
    | "captureLaneImpactMax"
  > = {},
): ObjectCardDefinition {
  return {
    id,
    name,
    nameKey: `card.${id}.name`,
    text,
    textKey: `card.${id}.text`,
    kind: "object",
    values,
    art,
    artId: `card.${id}`,
    ...options,
  };
}

function spellCard(
  id: string,
  name: string,
  effect: SpellEffect,
  text: string,
  art: string,
  targetType: SpellTargetType = effect === "wind" ? "object-direction" : "object",
): SpellCardDefinition {
  return {
    id,
    name,
    nameKey: `card.${id}.name`,
    text,
    textKey: `card.${id}.text`,
    kind: "spell",
    effect,
    targetType,
    art,
    artId: `card.${id}`,
  };
}

export const CARDS: readonly CardDefinition[] = [
  objectCard("apple", "赤いりんご", { 3: 1, 2: 2, 1: 3 }, "深く甘くなる、素直なよい夢。", "../../assets/cards/gardener/game/apple.jpg"),
  objectCard("snake", "白いへび", { 3: -1, 2: -2, 1: -3 }, "目覚めに近づくほど不安が濃くなる。", "../../assets/cards/gardener/game/snake.jpg"),
  objectCard("cloud", "雲のゆりかご", { 3: 2, 2: 1, 1: 0 }, "深い場所では安らぐが、朝にはほどける。", "../../assets/cards/caretaker/fullbleed-v1/game/fluffy-lullaby.jpg"),
  objectCard("thorn", "黒い茨", { 3: 0, 2: -1, 1: -3 }, "最初は気づかれず、浅い夢で牙をむく。", "../../assets/cards/shadowbinder/fullbleed-v1/game/black-lace-curtain.jpg"),
  objectCard("door", "閉ざされた扉", { 3: 1, 2: 0, 1: -2 }, "期待は、近づきすぎると恐れに変わる。", "../../assets/cards/shadowbinder/fullbleed-v1/game/stitched-door.jpg"),
  objectCard("candle", "帰りを待つ灯", { 3: -1, 2: 1, 1: 2 }, "遠くでは心細く、近くでは道しるべになる。", "../../assets/cards/station/fullbleed-v1/game/opposite-window-light.jpg"),
  spellCard("wind", "夢の風", "wind", "夢オブジェクトを左右へ1レーン動かす。端から出ると消える。", "../../assets/cards/gardener/game/fray.jpg"),
  spellCard("mirror", "鏡合わせ", "mirror", "夢オブジェクトの幸福度影響の符号を反転する。", "../../assets/cards/station/fullbleed-v1/game/mirror-reflection.jpg"),
  spellCard("sink", "もっと深く", "sink", "夢オブジェクトを深度+1。移動先に夢があれば合体する。", "../../assets/cards/gardener/game/tend.jpg"),
  spellCard("rise", "目覚めの気配", "rise", "夢オブジェクトを深度-1。深度0になると消える。", "../../assets/cards/refrain/fullbleed-v1/game/still-there-after-waking.jpg"),
  spellCard("hush", "静かな幕", "hush", "夢オブジェクトを次の時間経過で移動させない。", "../../assets/cards/caretaker/fullbleed-v1/game/gentle-restraint.jpg"),

  objectCard("story_fresh_grass", "新しい草の匂い", { 3: 1, 2: 0, 1: 0 }, "犬には新しい発見を、男には長すぎる道を感じさせる。", "../../assets/cards/gardener/game/sprout-dream.jpg"),
  objectCard("story_evening_scent", "夕暮れの草花", { 3: 1, 2: 2, 1: 3 }, "歩くほど濃くなり、犬を先へ誘う匂い。", "../../assets/cards/gardener/game/starlight.jpg"),
  objectCard("story_looping_path", "伸び続ける散歩道", { 3: -1, 2: 1, 1: 2 }, "深い夜には不安を呼ぶが、朝へ近づけば帰り道にもなる。", "../../assets/story/chapters/chapter-01/cards/looping-path-v1.png"),
  objectCard("red_ball", "赤いボール", { 3: 1, 2: 1, 1: 1 }, "夢を去った次の時間、一度だけ中央の深みへ帰ってくる。", "../../assets/story/chapters/chapter-01/cards/red-ball-v1.png", { returnsOnce: true }),

  objectCard("story_sturdy_rope", "丈夫なロープ", { 3: 1, 2: 1, 1: 0 }, "怖さを消さず、二人が同じ橋を渡るための備え。", "../../assets/story/chapters/chapter-02/cards/sturdy-rope-v1.png"),
  objectCard("story_return_map", "帰り道の地図", { 3: 0, 2: 1, 1: 2 }, "遠くでは頼りないが、朝へ近づくほど帰路を思い出す。", "../../assets/story/chapters/chapter-02/cards/return-map-v1.png"),
  objectCard("story_hero_boots", "格好よいブーツ", { 3: 2, 2: 1, 1: 0 }, "少年の足取りを軽くし、山頂への期待をふくらませる。", "../../assets/story/chapters/chapter-02/cards/hero-boots-v1.png"),
  objectCard("story_honey_ration", "蜂蜜の携帯食", { 3: 1, 2: 1, 1: 0 }, "もう一歩だけ先へ進みたくなる、甘い旅の食事。", "../../assets/story/chapters/chapter-02/cards/honey-ration-v1.png"),
  objectCard("story_hero_cloak", "勇者のマント", { 3: 1, 2: 1, 1: 0 }, "引き返す理由を覆い隠す、金糸の立派なマント。", "../../assets/story/chapters/chapter-02/cards/hero-cloak-v1.png"),
  objectCard("story_distant_roar", "遠い咆哮", { 3: -1, 2: -2, 1: -3 }, "近づくほど、竜の声は期待より恐れとして響く。", "../../assets/story/chapters/chapter-02/cards/distant-roar-v1.png"),
  objectCard("story_first_aid", "救急道具", { 3: 0, 2: 0, 1: 0 }, "少女が自分で用意していた持ち物。単独では幸福度を動かさない。", "../../assets/story/chapters/chapter-02/cards/first-aid-kit-v1.png", { persistent: true }),
  objectCard("story_injured_dragon", "傷ついたドラゴン", { 3: -2, 2: -2, 1: -3 }, "痛みと警戒に満ちた竜。救急道具と二人の注目を必要としている。", "../../assets/story/chapters/chapter-02/cards/injured-dragon-v1.png"),
  objectCard("story_flying_dragon", "空を飛ぶドラゴン", { 3: 3, 2: 2, 1: 1 }, "手当てを終え、二人を背に乗せた竜。大きな羽音が夢を満たす。", "../../assets/story/chapters/chapter-02/cards/flying-dragon-v1.png"),

  objectCard("greenhouse_sprout", "夜露の芽", { 3: 0, 2: 1, 1: 2 }, "すぐには実らないが、目覚めへ向かって育つ。", "../../assets/cards/gardener/game/sprout-dream.jpg"),
  objectCard("night_flower", "夜咲きの花", { 3: 1, 2: 2, 1: 1 }, "夜の半ばにもっとも甘く香る。", "../../assets/cards/gardener/game/starlight.jpg"),
  objectCard("sleeping_moss", "まどろみ苔", { 3: -1, 2: 0, 1: 1 }, "遠くでは迷い、近くでは柔らかな足場になる。", "../../assets/cards/gardener/game/soil-blanket.jpg"),
  spellCard("gardener_tend", "手入れ", "grow", "夢オブジェクトを符号方向へ恒久的に1育てる。", "../../assets/cards/gardener/game/tend.jpg"),
  spellCard("pruning_shears", "剪定ばさみ", "prune", "夢オブジェクトの絶対値を恒久的に1小さくする。", "../../assets/cards/gardener/game/pruning-shears.jpg"),
  spellCard("transplant", "植え替え", "wind", "夢オブジェクトを左右へ1レーン移す。端から出ると消える。", "../../assets/cards/gardener/game/fray.jpg"),
  spellCard("soil_blanket", "土をかぶせる", "sink", "夢オブジェクトを深度+1。移動先に夢があれば合体する。", "../../assets/cards/gardener/game/soil-blanket.jpg"),

  objectCard("mirror_passenger", "鏡の乗客", { 3: 1, 2: 1, 1: 1 }, "どこにいても同じ微笑みを浮かべている。", "../../assets/cards/station/fullbleed-v1/game/mirror-passenger.jpg"),
  objectCard("lost_pair", "迷子のふたり", { 3: -1, 2: -1, 1: -1 }, "二つの不安が、同じホームをさまよう。", "../../assets/cards/station/fullbleed-v1/game/lost-pair.jpg"),
  objectCard("mirror_gate_object", "鏡の改札", { 3: 0, 2: 1, 1: 2 }, "近づくほど、出口らしく見えてくる。", "../../assets/cards/station/fullbleed-v1/game/mirror-gate.jpg"),
  objectCard("station_fog", "立ちこめる霧", { 3: 0, 2: -1, 1: -2 }, "浅いホームほど、行き先を覆い隠す。", "../../assets/cards/station/fullbleed-v1/game/station-fog.jpg"),
  objectCard("crowded_platform", "満員ホーム", { 3: 2, 2: 1, 1: 0 }, "遠くでは高揚し、出口では人波にほどける。", "../../assets/cards/station/fullbleed-v1/game/crowded-platform.jpg"),
  spellCard("transfer_guide", "乗換案内", "wind", "夢オブジェクトを左右へ1レーン動かす。端では反対側へ折り返す。", "../../assets/cards/station/fullbleed-v1/game/transfer-guide.jpg"),
  spellCard("mirror_reflection", "鏡像反転", "mirror", "夢オブジェクトの幸福度影響の符号を反転する。", "../../assets/cards/station/fullbleed-v1/game/mirror-reflection.jpg"),
  spellCard("turnaround_train", "折返し列車", "turnaround", "端の夢を鏡の外へ送り、反転して反対端へ戻す。中央の夢ならその場で反転する。", "../../assets/cards/station/fullbleed-v1/game/turnaround-train.jpg"),
  spellCard("departure_bell", "発車ベル", "rise", "夢オブジェクトを深度-1。深度0になると消える。", "../../assets/cards/station/fullbleed-v1/game/departure-bell.jpg"),

  objectCard("familiar_lamppost", "見覚えのある街灯", { 3: -1, 2: 0, 1: 2 }, "遠くでは不気味だが、近づくほど帰り道の目印になる。", ""),
  spellCard("one_more_lap", "もう一周", "return-deep", "夢オブジェクト1つを同じレーンの深度3へ戻す。", "", "object"),
  spellCard("way_home", "帰り道", "homeward", "指定レーンの全オブジェクトを一括で深度1へ移す。", "", "lane"),

  objectCard("burnt_cookies", "焼きすぎたクッキー", { 3: 2, 2: 1, 1: -1 }, "甘い匂いのあとから、少し苦い失敗が追いついてくる。", ""),
  objectCard("hearth_fire", "暖炉の火", { 3: 1, 2: 2, 1: 2 }, "浅い夢へ届いても、一度だけその場に残る温かな火。", "", { surfaceHolds: 1 }),
  spellCard("falling_snow", "降り積もる雪", "freeze-depth", "指定深度の全オブジェクトを、この時間の終わりに移動させない。", "", "depth"),
  spellCard("rewrapped_gift", "包み直した贈り物", "rewrap", "夢オブジェクトを一時退場させ、次の時間に同じレーンの深度3へ戻す。", "", "object"),

  objectCard("street_piano", "ストリートピアノ", { 3: 0, 2: 0, 1: 0 }, "二人が同じレーンへ注目すると、双方へ幸福度+2を届ける。", "", { persistent: true, resonanceBonus: 2 }),
  objectCard("faceless_crowd", "人垣", { 3: -1, 2: -2, 1: -3 }, "浅い夢ほど視線が増え、演奏者を舞台へ縫い留める。", ""),
  spellCard("encore", "アンコール", "encore", "夢オブジェクト1つの幸福度影響を、この時間だけ2倍にする。", "", "object"),
  spellCard("turn_back_clock", "時計の針を戻す", "rewind-all", "盤面の全オブジェクトを一括で深度+1する。", "", "none"),

  objectCard("stubborn_stain", "取れない染み", { 3: 0, 2: -1, 1: -2 }, "時間が進んでも、磨き残した場所に留まり続ける。", "", { persistent: true }),
  objectCard("steaming_tea", "湯気の立つ紅茶", { 3: 0, 2: 1, 1: 2 }, "同じ場所に残り、古い部屋へ穏やかな記憶を満たす。", "", { persistent: true }),
  spellCard("layered_time", "積み重なる時間", "layer-time", "夢オブジェクトを、同じレーンの隣接深度へ複製する。", "", "object-depth"),
  spellCard("long_holiday", "長い休暇", "dismiss", "夢オブジェクト1つを、その夜の盤面から除外する。", "", "object"),

  objectCard("lingering_note", "残響", { 3: 1, 2: 1, 1: 1 }, "この夢が合体すると、合体値を符号方向へさらに1強める。", "", { mergeBonus: 1 }),
  spellCard("rest_note", "休符", "rest", "夢オブジェクト1つの影響と時間経過を、この時間だけ止める。", "", "object"),
  spellCard("harmony", "和音", "harmony", "指定深度の両端にある夢を中央へ寄せ、重なれば合体させる。", "", "depth"),
  objectCard("seventh_note", "七つ目の音", { 3: 0, 2: 0, 1: 0 }, "置いたレーンの現在影響を、絶対値4まで写し取る。", "", { captureLaneImpactMax: 4 }),
];

const cardMap = new Map(CARDS.map((card) => [card.id, card]));

export function getCard(cardId: string): CardDefinition | null {
  return cardMap.get(cardId) ?? null;
}
